import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './index.scss';

interface Article {
  id: string;
  title: string;
  description: string;
  date: string;
  category: string;
  image: string;
  content: string;
}

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // 模拟文章数据，实际项目中应该从API获取
  const articles: Article[] = [
    {
      id: '1',
      title: 'React 19 新特性详解',
      description: '探索 React 19 的最新特性，包括 Server Components、Actions 和更多激动人心的功能。',
      date: '2026-03-10',
      category: '前端开发',
      image: 'https://picsum.photos/seed/react19/1200/675',
      content: `
        <h2>React 19 简介</h2>
        <p>React 19 带来了许多令人兴奋的新特性，这些特性将改变我们构建 React 应用的方式。本文将详细介绍这些新特性，帮助你快速上手 React 19。</p>
        
        <h3>1. Server Components</h3>
        <p>Server Components 是 React 19 中最引人注目的新特性之一。它允许你在服务器端渲染组件，减少客户端的 JavaScript 包大小，提高应用性能。</p>
        <p>使用 Server Components，你可以：</p>
        <ul>
          <li>减少客户端 JavaScript 包大小</li>
          <li>提高首屏加载速度</li>
          <li>更好地处理数据获取</li>
          <li>改善 SEO</li>
        </ul>
        
        <h3>2. Actions</h3>
        <p>Actions 是 React 19 中另一个重要的新特性，它简化了表单处理和服务器端操作。</p>
        <p>通过 Actions，你可以：</p>
        <ul>
          <li>轻松处理表单提交</li>
          <li>管理加载状态</li>
          <li>处理错误</li>
          <li>与服务器端函数集成</li>
        </ul>
        
        <h3>3. 改进的 Suspense</h3>
        <p>React 19 对 Suspense 进行了重大改进，使其更加稳定和强大。</p>
        <p>新的 Suspense 特性包括：</p>
        <ul>
          <li>更好的错误边界集成</li>
          <li>更灵活的加载状态管理</li>
          <li>支持并发渲染</li>
        </ul>
        
        <h3>4. 其他改进</h3>
        <p>React 19 还包括许多其他改进，如：</p>
        <ul>
          <li>改进的开发体验</li>
          <li>更好的类型推断</li>
          <li>性能优化</li>
          <li>新的 Hook</li>
        </ul>
        
        <h2>结论</h2>
        <p>React 19 是一个重大的版本更新，带来了许多令人兴奋的新特性。通过使用 Server Components、Actions 和改进的 Suspense，你可以构建更高效、更可靠的 React 应用。</p>
        <p>如果你还没有尝试 React 19，现在是时候开始探索这些新特性了！</p>
      `
    },
    {
      id: '2',
      title: 'TypeScript 5.0 实用技巧',
      description: '掌握 TypeScript 5.0 的高级特性，提升代码质量和开发效率。',
      date: '2026-03-05',
      category: '前端开发',
      image: 'https://picsum.photos/seed/typescript5/1200/675',
      content: `
        <h2>TypeScript 5.0 简介</h2>
        <p>TypeScript 5.0 带来了许多新特性和改进，使 TypeScript 更加强大和易用。本文将介绍一些 TypeScript 5.0 的实用技巧，帮助你提升代码质量和开发效率。</p>
        
        <h3>1. 模板字面量类型</h3>
        <p>TypeScript 5.0 增强了模板字面量类型，使其更加灵活和强大。</p>
        <pre><code>type EventName = \`on\${Capitalize&lt;string&gt;}\`;
const onClick: EventName = 'onClick'; // 正确
const onMouseOver: EventName = 'onMouseOver'; // 正确
const onfoo: EventName = 'onfoo'; // 错误，应该是 'onFoo'</code></pre>
        
        <h3>2. 装饰器</h3>
        <p>TypeScript 5.0 正式支持装饰器，这是一个期待已久的特性。</p>
        <pre><code>function Log(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function(...args: any[]) {
    console.log(\`Calling \${propertyKey} with arguments: \${args}\`);
    return originalMethod.apply(this, args);
  };
  return descriptor;
}

class Calculator {
  @Log
  add(a: number, b: number): number {
    return a + b;
  }
}</code></pre>
        
        <h3>3. 改进的类型推断</h3>
        <p>TypeScript 5.0 改进了类型推断，使其更加智能和准确。</p>
        <pre><code>// TypeScript 4.x
const obj = { a: 1, b: 'hello' };
// obj 的类型是 { a: number; b: string; }

// TypeScript 5.0
const obj = { a: 1, b: 'hello' as const };
// obj 的类型是 { a: 1; b: 'hello'; }</code></pre>
        
        <h3>4. 其他技巧</h3>
        <p>TypeScript 5.0 还有许多其他实用技巧，如：</p>
        <ul>
          <li>使用 const 断言获得更精确的类型</li>
          <li>使用 satisfies 操作符确保类型兼容性</li>
          <li>使用类型守卫提高代码安全性</li>
          <li>使用泛型约束增强类型安全性</li>
        </ul>
        
        <h2>结论</h2>
        <p>TypeScript 5.0 是一个重大的版本更新，带来了许多实用的新特性和改进。通过掌握这些技巧，你可以编写更安全、更可维护的代码，提升开发效率。</p>
        <p>如果你还没有升级到 TypeScript 5.0，现在是时候考虑了！</p>
      `
    },
    {
      id: '3',
      title: '现代 CSS 布局技术',
      description: '深入了解 Grid、Flexbox 和最新的 CSS 布局技术，创建响应式设计。',
      date: '2026-02-28',
      category: '前端开发',
      image: 'https://picsum.photos/seed/cssgrid/1200/675',
      content: `
        <h2>现代 CSS 布局技术</h2>
        <p>CSS 布局技术在过去几年中取得了巨大的进步，从传统的浮动布局到现代的 Flexbox 和 Grid，我们有了更多强大的工具来创建复杂的布局。本文将深入介绍这些现代 CSS 布局技术，帮助你创建响应式、灵活的设计。</p>
        
        <h3>1. Flexbox</h3>
        <p>Flexbox 是一种一维布局模型，非常适合处理行或列的布局。</p>
        <pre><code>.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.flex-item {
  flex: 1;
  min-width: 200px;
}</code></pre>
        
        <h3>2. Grid</h3>
        <p>Grid 是一种二维布局模型，非常适合处理复杂的网格布局。</p>
        <pre><code>.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  grid-auto-rows: minmax(200px, auto);
}

.grid-item {
  grid-column: span 1;
  grid-row: span 1;
}

@media (min-width: 768px) {
  .grid-item.featured {
    grid-column: span 2;
    grid-row: span 2;
  }
}</code></pre>
        
        <h3>3. 响应式设计</h3>
        <p>现代 CSS 布局技术使响应式设计变得更加容易。</p>
        <pre><code>/* 移动设备优先 */
.container {
  padding: 1rem;
}

/* 平板设备 */
@media (min-width: 768px) {
  .container {
    padding: 2rem;
    max-width: 960px;
    margin: 0 auto;
  }
}

/* 桌面设备 */
@media (min-width: 1200px) {
  .container {
    max-width: 1200px;
  }
}</code></pre>
        
        <h3>4. 其他现代 CSS 特性</h3>
        <p>除了 Flexbox 和 Grid，现代 CSS 还有许多其他强大的特性，如：</p>
        <ul>
          <li>CSS 变量（自定义属性）</li>
          <li>CSS 网格区域</li>
          <li>CSS 书写模式</li>
          <li>CSS 逻辑属性</li>
        </ul>
        
        <h2>结论</h2>
        <p>现代 CSS 布局技术为我们提供了强大的工具来创建复杂、响应式的设计。通过掌握 Flexbox、Grid 和其他现代 CSS 特性，你可以创建更加灵活、美观的布局，提升用户体验。</p>
        <p>如果你还没有充分利用这些现代 CSS 布局技术，现在是时候开始学习和应用它们了！</p>
      `
    },
    {
      id: '4',
      title: 'Web 性能优化指南',
      description: '学习如何优化网站性能，提升用户体验和搜索引擎排名。',
      date: '2026-02-20',
      category: '性能优化',
      image: 'https://picsum.photos/seed/webperf/1200/675',
      content: `
        <h2>Web 性能优化指南</h2>
        <p>网站性能是用户体验和搜索引擎排名的关键因素。一个快速加载的网站不仅能提供更好的用户体验，还能提高转化率和搜索引擎排名。本文将介绍一些实用的 Web 性能优化技巧，帮助你提升网站速度。</p>
        
        <h3>1. 资源优化</h3>
        <p>优化网站资源是提高性能的基础。</p>
        <ul>
          <li><strong>压缩文件</strong>：使用 Gzip 或 Brotli 压缩 HTML、CSS 和 JavaScript 文件。</li>
          <li><strong>优化图片</strong>：使用适当的图片格式（如 WebP），压缩图片，使用懒加载。</li>
          <li><strong>最小化代码</strong>：移除不必要的代码，使用工具如 Terser 最小化 JavaScript。</li>
          <li><strong>使用 CDN</strong>：使用内容分发网络 (CDN) 分发静态资源。</li>
        </ul>
        
        <h3>2. 加载优化</h3>
        <p>优化资源加载顺序和方式可以显著提高网站速度。</p>
        <ul>
          <li><strong>关键 CSS</strong>：将关键 CSS 内联到 HTML 中，确保首屏快速渲染。</li>
          <li><strong>异步加载</strong>：使用 async 或 defer 属性加载 JavaScript 文件。</li>
          <li><strong>预加载</strong>：使用 preload 预加载关键资源。</li>
          <li><strong>预连接</strong>：使用 preconnect 预连接到重要的域名。</li>
        </ul>
        
        <h3>3. 缓存策略</h3>
        <p>合理的缓存策略可以减少重复请求，提高网站速度。</p>
        <ul>
          <li><strong>浏览器缓存</strong>：设置适当的 Cache-Control 头。</li>
          <li><strong>服务器缓存</strong>：使用服务器端缓存如 Redis。</li>
          <li><strong>内容缓存</strong>：使用 CDN 缓存静态内容。</li>
        </ul>
        
        <h3>4. 性能监控</h3>
        <p>定期监控网站性能是持续优化的关键。</p>
        <ul>
          <li><strong>Core Web Vitals</strong>：监控 Google 的 Core Web Vitals 指标。</li>
          <li><strong>性能测试工具</strong>：使用 Lighthouse、WebPageTest 等工具测试网站性能。</li>
          <li><strong>实时监控</strong>：使用 New Relic、Datadog 等工具实时监控网站性能。</li>
        </ul>
        
        <h2>结论</h2>
        <p>Web 性能优化是一个持续的过程，需要不断地监测和改进。通过实施本文介绍的优化技巧，你可以显著提高网站速度，提升用户体验和搜索引擎排名。</p>
        <p>记住，性能优化没有终点，总是有改进的空间。定期检查和优化你的网站，确保它始终保持最佳状态。</p>
      `
    },
    {
      id: '5',
      title: 'Node.js 最新特性',
      description: '探索 Node.js 的最新版本和特性，构建高性能后端应用。',
      date: '2026-02-15',
      category: '后端开发',
      image: 'https://picsum.photos/seed/nodejs/1200/675',
      content: `
        <h2>Node.js 最新特性</h2>
        <p>Node.js 一直在不断发展和改进，为开发者提供更强大、更高效的后端开发体验。本文将介绍 Node.js 的最新特性，帮助你构建高性能的后端应用。</p>
        
        <h3>1. 原生 ES 模块</h3>
        <p>Node.js 现在完全支持原生 ES 模块，无需使用 Babel 或其他转译工具。</p>
        <pre><code>// package.json
{
  "type": "module"
}

// 导入模块
import fs from 'fs/promises';
import express from 'express';

// 导出模块
export function hello() {
  return 'Hello World';
}</code></pre>
        
        <h3>2. 异步本地存储</h3>
        <p>Node.js 提供了异步本地存储 API，用于在 Worker 线程之间共享数据。</p>
        <pre><code>import { AsyncLocalStorage } from 'async_hooks';

const asyncLocalStorage = new AsyncLocalStorage();

// 设置存储
asyncLocalStorage.run({ userId: 123 }, () => {
  // 在这个上下文中执行的所有函数都可以访问存储
  console.log(asyncLocalStorage.getStore()); // { userId: 123 }
});</code></pre>
        
        <h3>3. 性能改进</h3>
        <p>Node.js 最新版本在性能方面有显著改进。</p>
        <ul>
          <li>更快的启动时间</li>
          <li>更低的内存使用</li>
          <li>改进的事件循环</li>
          <li>更快的网络 I/O</li>
        </ul>
        
        <h3>4. 新的 API</h3>
        <p>Node.js 引入了许多新的 API，使开发更加便捷。</p>
        <ul>
          <li><strong>AbortController</strong>：用于取消异步操作</li>
          <li><strong>Promise.any()</strong>：等待第一个成功的 Promise</li>
          <li><strong>top-level await</strong>：在模块顶层使用 await</li>
          <li><strong>Web Crypto API</strong>：提供加密功能</li>
        </ul>
        
        <h2>结论</h2>
        <p>Node.js 的最新特性为后端开发提供了更强大、更高效的工具。通过利用这些新特性，你可以构建性能更好、更可靠的后端应用。</p>
        <p>如果你还没有升级到最新版本的 Node.js，现在是时候考虑了！</p>
      `
    },
    {
      id: '6',
      title: 'AI 在前端开发中的应用',
      description: '了解人工智能如何改变前端开发，以及如何使用 AI 工具提升开发效率。',
      date: '2026-02-10',
      category: '人工智能',
      image: 'https://picsum.photos/seed/aidev/1200/675',
      content: `
        <h2>AI 在前端开发中的应用</h2>
        <p>人工智能 (AI) 正在改变各行各业，前端开发也不例外。从代码生成到用户体验优化，AI 工具正在帮助前端开发者提高效率、创造更好的产品。本文将介绍 AI 在前端开发中的应用，以及如何使用 AI 工具提升开发效率。</p>
        
        <h3>1. 代码生成</h3>
        <p>AI 工具可以帮助你生成代码，减少重复性工作。</p>
        <ul>
          <li><strong>智能代码补全</strong>：如 GitHub Copilot，根据上下文生成代码建议。</li>
          <li><strong>代码转换</strong>：将一种语言或框架的代码转换为另一种。</li>
          <li><strong>单元测试生成</strong>：自动生成单元测试代码。</li>
        </ul>
        
        <h3>2. 设计辅助</h3>
        <p>AI 工具可以帮助你设计更好的用户界面。</p>
        <ul>
          <li><strong>UI 生成</strong>：根据需求生成 UI 组件。</li>
          <li><strong>颜色方案</strong>：生成协调的颜色方案。</li>
          <li><strong>响应式设计</strong>：自动调整设计以适应不同屏幕尺寸。</li>
        </ul>
        
        <h3>3. 用户体验优化</h3>
        <p>AI 可以帮助你创建更个性化、更智能的用户体验。</p>
        <ul>
          <li><strong>个性化内容</strong>：根据用户行为推荐内容。</li>
          <li><strong>智能搜索</strong>：提供更准确的搜索结果。</li>
          <li><strong>聊天机器人</strong>：提供即时客户支持。</li>
        </ul>
        
        <h3>4. 性能优化</h3>
        <p>AI 工具可以帮助你优化网站性能。</p>
        <ul>
          <li><strong>代码优化</strong>：识别并优化性能瓶颈。</li>
          <li><strong>资源压缩</strong>：智能压缩图片和其他资源。</li>
          <li><strong>加载优化</strong>：优化资源加载顺序。</li>
        </ul>
        
        <h2>结论</h2>
        <p>AI 正在成为前端开发的重要工具，帮助开发者提高效率、创造更好的产品。通过利用 AI 工具，你可以专注于更有创造性的工作，而不是重复性的任务。</p>
        <p>然而，AI 工具只是辅助工具，不能完全替代人类开发者的创造力和判断。在使用 AI 工具时，你仍然需要理解代码的工作原理，并确保代码质量和安全性。</p>
      `
    }
  ];

  // 根据ID查找文章
  const article = articles.find(a => a.id === id);

  if (!article) {
    return (
      <div className="article-detail">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="not-found"
          >
            <h2>文章未找到</h2>
            <p>抱歉，找不到您请求的文章。</p>
            <Link to="/" className="btn-back">返回首页</Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="article-detail">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="article-header"
        >
          <div className="article-category">{article.category}</div>
          <h1 className="article-title">{article.title}</h1>
          <div className="article-meta">
            <span className="article-date">{article.date}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="article-image"
        >
          <img src={article.image} alt={article.title} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="article-content"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="article-footer"
        >
          <Link to="/" className="btn-back">返回首页</Link>
        </motion.div>
      </div>
    </div>
  );
};

export default ArticleDetail;